import DataGridTable from '@/components/shared/DataGridTable'
import { columns as orderColumns } from '@/components/pages/orders/columns'
import { columns as patientColumns } from '@/components/pages/patients/columns'
import { columns as userColumns } from '@/components/pages/users/columns'
import { buildTextFilterParam } from '@/lib/dataGridFilters'
import { toE164 } from '@/lib/phone'
import { useOrdersQuery } from '@/services/orders'
import { usePatientsQuery } from '@/services/patients'
import { useUsersQuery } from '@/services/users'
import { colors, fontSizes, fontWeights } from '@/theme'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

const SEARCH_RESULTS_LIMIT = 3

const ORDERS_SEARCH_ATTRIBUTES: Partial<Record<SearchType, string>> = {
  order: JSON.stringify(['order_number']),
  email: JSON.stringify(['billing_email']),
  phone: JSON.stringify(['shipping_phone']),
  postcode: JSON.stringify(['shipping_postcode']),
  general: JSON.stringify(['billing_email', 'billing_full_name'])
}

const ORDER_NUMBER_PREFIXES = [
  'es',
  'as',
  'un',
  'rl',
  'jude',
  'ep',
  'lh',
  'avid',
  'pt',
  'ev',
  'ptp',
  'dc',
  'lhp',
  'seppfc',
  'ch',
  'ut',
  '28x',
  'svi',
  'pi',
  'hwf'
]
const ORDER_NUMBER_PATTERN = new RegExp(`^(staging-|development-)?(${ORDER_NUMBER_PREFIXES.join('|')})-`, 'i')
const UK_POSTCODE_PATTERN = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i

// Require some content after the prefix (not just "es-") so we don't commit to an
// order search — and fire a real query — before the agent has typed more than the prefix.
const MIN_ORDER_SUFFIX_LENGTH = 3

const isOrderNumberQuery = (query: string) => {
  const match = query.match(ORDER_NUMBER_PATTERN)
  return !!match && query.length >= match[0].length + MIN_ORDER_SUFFIX_LENGTH
}
const isValidEmail = (query: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query)
const isValidUKPhone = (query: string) => toE164(query) !== null
const isValidUKPostcode = (query: string) => UK_POSTCODE_PATTERN.test(query.trim())

export type SearchType = 'order' | 'email' | 'phone' | 'postcode' | 'general'

export function getSearchType(query: string): SearchType {
  if (isOrderNumberQuery(query)) return 'order'
  if (isValidEmail(query)) return 'email'
  if (isValidUKPhone(query)) return 'phone'
  if (isValidUKPostcode(query)) return 'postcode'
  return 'general'
}

function SectionHeader({ title, count, viewAllHref }: { title: string; count: number; viewAllHref: string }) {
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.5
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: fontWeights.semibold,
            color: colors.text.primary
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            bgcolor: colors.grey[100],
            borderRadius: '6px',
            px: 1,
            py: 0.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px'
          }}
        >
          <Typography
            sx={{
              fontSize: fontSizes.md,
              fontWeight: fontWeights.semibold,
              color: colors.text.secondary
            }}
          >
            {count}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant='link'
        onClick={() => navigate(viewAllHref)}
        sx={{ '&:hover': { textDecoration: 'underline' } }}
      >
        View all
      </Typography>
    </Box>
  )
}

function buildPatientSearchParams(
  rawQuery: string,
  searchType: SearchType
): NonNullable<Parameters<typeof usePatientsQuery>[0]>['params'] {
  const query = rawQuery.trim()

  if (searchType === 'phone') {
    return {
      phoneFilter: buildTextFilterParam({
        operator: 'startsWith',
        value: toE164(query)!
      }),
      limit: SEARCH_RESULTS_LIMIT,
      skipCount: true
    }
  }

  if (searchType === 'email') {
    return {
      emailFilter: buildTextFilterParam({
        operator: 'startsWith',
        value: query
      }),
      limit: SEARCH_RESULTS_LIMIT,
      skipCount: true
    }
  }

  if (searchType === 'postcode') {
    return {
      postcodeFilter: buildTextFilterParam({
        operator: 'startsWith',
        value: query
      }),
      limit: SEARCH_RESULTS_LIMIT,
      skipCount: true
    }
  }

  return {
    searchQuery: query,
    searchMode: 'startsWith',
    searchAttributes: JSON.stringify(['first_name', 'last_name', 'email']),
    limit: SEARCH_RESULTS_LIMIT,
    skipCount: true
  }
}

function buildPatientViewAllHref(rawQuery: string, searchType: SearchType): string {
  const query = rawQuery.trim()

  if (searchType === 'email') {
    const filters = [{ field: 'email', operator: 'startsWith', value: query }]
    return `/patients?filters=${encodeURIComponent(JSON.stringify(filters))}`
  }
  if (searchType === 'phone') {
    const filters = [{ field: 'phone', operator: 'startsWith', value: toE164(query)! }]
    return `/patients?filters=${encodeURIComponent(JSON.stringify(filters))}`
  }

  return `/patients?searchQuery=${encodeURIComponent(query)}`
}

function PatientsResults({ searchQuery, searchType }: { searchQuery: string; searchType: SearchType }) {
  const navigate = useNavigate()
  const { patients, count, isLoading, isFetching } = usePatientsQuery({
    tableId: 'dashboard-search-patients',
    enabled: !!searchQuery,
    params: buildPatientSearchParams(searchQuery, searchType)
  })

  if (isLoading) return <CircularProgress size={28} sx={{ mx: 'auto', display: 'block', my: 4 }} />
  if (count === 0) return null

  return (
    <Box>
      <SectionHeader title='Patients' count={count} viewAllHref={buildPatientViewAllHref(searchQuery, searchType)} />
      <Box
        sx={{ opacity: isFetching ? 0.5 : 1, pointerEvents: isFetching ? 'none' : 'auto', transition: 'opacity 0.15s' }}
      >
        <DataGridTable
          compactMode
          rows={patients}
          columns={patientColumns}
          rowCount={count}
          loading={isFetching}
          onRowClick={params => {
            if (isFetching) return
            navigate(`/patients/${params.id}`, {
              state: { backLabel: 'Back to Dashboard', fallbackPath: '/' }
            })
          }}
        />
      </Box>
    </Box>
  )
}

function UsersResults({ searchQuery, searchType }: { searchQuery: string; searchType: SearchType }) {
  const navigate = useNavigate()

  const phoneE164 = searchType === 'phone' ? toE164(searchQuery) : null
  const params = phoneE164
    ? {
        phoneFilter: buildTextFilterParam({
          operator: 'startsWith',
          value: phoneE164
        }),
        limit: SEARCH_RESULTS_LIMIT,
        skipCount: true
      }
    : searchType === 'email'
      ? {
          emailFilter: buildTextFilterParam({
            operator: 'startsWith',
            value: searchQuery
          }),
          limit: SEARCH_RESULTS_LIMIT,
          skipCount: true
        }
      : {
          searchQuery,
          searchMode: 'startsWith' as const,
          ...(searchType === 'general' && { searchAttributes: JSON.stringify(['email', 'display_name']) }),
          limit: SEARCH_RESULTS_LIMIT,
          skipCount: true
        }

  const { users, count, isLoading, isFetching } = useUsersQuery({
    tableId: 'dashboard-search-users',
    enabled: !!searchQuery,
    params
  })

  if (isLoading) return <CircularProgress size={28} sx={{ mx: 'auto', display: 'block', my: 4 }} />
  if (count === 0) return null

  return (
    <Box>
      <SectionHeader
        title='Users'
        count={count}
        viewAllHref={
          searchType === 'email'
            ? `/users?filters=${encodeURIComponent(
                JSON.stringify([{ field: 'email', operator: 'startsWith', value: searchQuery }])
              )}`
            : `/users?searchQuery=${encodeURIComponent(searchQuery)}`
        }
      />
      <Box
        sx={{ opacity: isFetching ? 0.5 : 1, pointerEvents: isFetching ? 'none' : 'auto', transition: 'opacity 0.15s' }}
      >
        <DataGridTable
          compactMode
          rows={users}
          columns={userColumns}
          rowCount={count}
          loading={isFetching}
          onRowClick={params => {
            if (isFetching) return
            navigate(`/users/${encodeURIComponent(params.row.email)}`, {
              state: { backLabel: 'Back to Dashboard', fallbackPath: '/' }
            })
          }}
        />
      </Box>
    </Box>
  )
}

function OrdersResults({ searchQuery, searchType }: { searchQuery: string; searchType: SearchType }) {
  const navigate = useNavigate()

  const searchAttributes = ORDERS_SEARCH_ATTRIBUTES[searchType]

  // Phone numbers are stored as E.164 in the DB — convert before searching
  const effectiveQuery = searchType === 'phone' ? (toE164(searchQuery) ?? searchQuery) : searchQuery

  const params = {
    searchQuery: effectiveQuery,
    searchMode: 'startsWith' as const,
    searchAttributes,
    limit: SEARCH_RESULTS_LIMIT,
    skipCount: true
  }

  const { orders, count, isLoading, isFetching } = useOrdersQuery({
    tableId: 'dashboard-search-orders',
    enabled: !!searchQuery,
    params
  })

  if (isLoading) return <CircularProgress size={28} sx={{ mx: 'auto', display: 'block', my: 4 }} />
  if (count === 0) return null

  return (
    <Box>
      <SectionHeader
        title='Orders'
        count={count}
        viewAllHref={
          searchType === 'email'
            ? `/orders?filters=${encodeURIComponent(
                JSON.stringify([{ field: 'billingEmail', operator: 'startsWith', value: searchQuery }])
              )}`
            : `/orders?searchQuery=${encodeURIComponent(searchQuery)}`
        }
      />
      <Box
        sx={{ opacity: isFetching ? 0.5 : 1, pointerEvents: isFetching ? 'none' : 'auto', transition: 'opacity 0.15s' }}
      >
        <DataGridTable
          compactMode
          rows={orders}
          columns={orderColumns}
          rowCount={count}
          loading={isFetching}
          onRowClick={params => {
            if (isFetching) return
            navigate(`/orders/${params.id}`, {
              state: { backLabel: 'Back to Dashboard', fallbackPath: '/' }
            })
          }}
        />
      </Box>
    </Box>
  )
}

export default function SearchResults({ searchQuery }: { searchQuery: string }) {
  const searchType = getSearchType(searchQuery)
  const showPatientsAndUsers =
    searchType === 'email' || searchType === 'phone' || searchType === 'postcode' || searchType === 'general'
  const showOrders = searchType === 'order' || showPatientsAndUsers

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {showPatientsAndUsers && <PatientsResults searchQuery={searchQuery} searchType={searchType} />}
      {showPatientsAndUsers && <UsersResults searchQuery={searchQuery} searchType={searchType} />}
      {showOrders && <OrdersResults searchQuery={searchQuery} searchType={searchType} />}
    </Box>
  )
}
